import {FC, useEffect, useMemo, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {maybeNotSetupRef} from "routes/private";
import useAccountant from "contexts/User/useAccountant";
import {activityStatusByDate} from "utils/activityStatusByDate";
import {lowActivityCutoff, highActivityCutoff} from "constants/xp";
import {availableCertifications, availableReviews} from "constants/certifications";
import accountantApi from "api/accountant";
import {handleError} from "helpers/errors";
import useAwaitableModal from "hooks/useAwaitableModal";
import CoursePicker from "./modals/CoursePicker";
import EditPassword from "./modals/EditPassword";
import EditProfile from "./modals/EditProfile";
import SubscribeToGym from "./modals/SubscribeToGym";
import CertificationPopup from "./modals/CertificationPopup";
import useAuthContext from "contexts/Auth/useAuthContext";
import {Star, Activity, Zap, Users, Share2} from "lucide-react";
import ShareModal from "./modals/ShareModal";
import {DASHBOARD_TARGET, CAS_FRONTEND_URL} from "api/axios";
import {getOverviewConfig, levelColor, levelLabel, knowledgeLabelMap} from "./utils";
import type {Level} from "./utils";
import StatusBar from "./components/StatusBar";
import ProfileSection from "./components/ProfileSection";
import ReviewsRow from "./components/ReviewsRow";
import ProfileTabs from "./components/ProfileTabs";
import ProgressTags from "./components/ProgressTags";
import Tooltip from "components/Tooltip";

const editableProfileFields = [
  "name",
  "bio",
  "state",
  "city",
  "employmentType",
  "zip",
  "softwareExperience",
  "linkedIn",
  "alignable",
] as const;

const AccountantProfilePage: FC = () => {
  const user = useAccountant();
  const auth = useAuthContext();
  const [sharing, setSharing] = useState(false);
  const [shareData, setShareData] = useState<{token: string; shareUrl: string} | null>(null);
  const [isStale, setIsStale] = useState(false);

  const skillsHash = user.activityOverview.skillsEarned
    .map((s) => s.createdAt)
    .sort()
    .join(",");
  const storageKey = auth?.uid ? `booksmatch_shared_skills_${auth.uid}` : null;

  useEffect(() => {
    if (!storageKey) return;
    const saved = localStorage.getItem(storageKey);
    setIsStale(saved !== null && saved !== skillsHash);
  }, [skillsHash, storageKey]);
  const [updating, setUpdating] = useState(false);
  const [openingGym, setOpeningGym] = useState(false);
  const [moreInfoOpen, setMoreInfoOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<"applied" | "foundational">(
    searchParams.get("tab") === "foundational" ? "foundational" : "applied",
  );
  const setAccountant = maybeNotSetupRef.current?.setUser as any;
  const editable = !!setAccountant;

  const [editProfile, EditProfileModal] = useAwaitableModal(EditProfile, {} as any);
  const [editPassword, EditPasswordModal] = useAwaitableModal(EditPassword, {} as any);
  const [_showCoursePicker, CoursePickerModal] = useAwaitableModal(CoursePicker, {} as any);
  const [subscribe, SubscribeToGymModal] = useAwaitableModal(SubscribeToGym, {});

  const knowledge: Level = user.skillScore > 80 ? "high" : user.skillScore > 20 ? "average" : "low";
  const activity: Level = activityStatusByDate(user.xpData.lastActivity || user.xpData.dateJoined);
  const speed: Level = user.speed;

  const activityDescription = useMemo(() => {
    if (activity === "low") return "Over " + lowActivityCutoff + " days ago";
    if (activity === "average") return "Within " + lowActivityCutoff + " days";
    return "Within " + highActivityCutoff + " days";
  }, [activity]);

  const addedPastWeek = user.activityOverview.skillsEarned.filter((skill) => {
    const diffDays = Math.ceil(Math.abs(Date.now() - new Date(skill.createdAt).getTime()) / 86400000);
    return diffDays <= 7;
  }).length;

  const totalSkills = user.activityOverview.skillsEarned.length;
  const isPaidMember = user.id !== auth?.uid || !!user.hasBooksMatchAccess;
  const showCopyMagic = user.enterprise && auth?.uid === user.enterprise;

  const [_progress, missing] = useMemo(() => {
    const nameFilled = user.name ? 1 : 0;
    const locationFilled = user.location ? 1 : 0;
    const bioFilled = user.bio ? 1 : 0;
    const employmentTypeFilled = user.employmentType ? 1 : 0;
    const softwareExperienceFilled = Object.values(user.softwareExperience).filter(Boolean).length ? 1 : 0;
    const certifications = Object.values(user.certifications).filter(Boolean).length;
    const imageFilled = user.image ? 1 : 0;
    const reviewsFilled = Object.values(user.reviews || {}).filter(Boolean).length;
    const linkedInFilled = user.linkedIn ? 1 : 0;
    const alignableFilled = user.alignable ? 1 : 0;
    const sum =
      nameFilled +
      locationFilled +
      bioFilled +
      employmentTypeFilled +
      softwareExperienceFilled +
      certifications +
      imageFilled +
      reviewsFilled +
      linkedInFilled +
      alignableFilled;
    const total = 10 + availableCertifications.length + availableReviews.length;
    const missingList = [
      !nameFilled && "Add your name",
      !locationFilled && "Add your location",
      !bioFilled && "Add a bio",
      !employmentTypeFilled && "Add your employment type",
      !softwareExperienceFilled && "Add your software experience",
      !imageFilled && "Add a profile picture",
      !linkedInFilled && "Add your LinkedIn",
      !alignableFilled && "Add your Alignable",
      ...availableCertifications.map((n) => !user.certifications[n] && `Add ${n}`),
      ...availableReviews.map((n) => !user.reviews?.[n] && `Add ${n}`),
    ].filter(Boolean) as string[];
    return [sum / total, missingList];
  }, [user]);

  /* ── handlers ───────────────────────────────────────────────────────────── */
  const onVisibilityToggle = () => {
    if (!editable || !isPaidMember) return void subscribe().catch(() => {});
    if (updating || user.enterprise) return;
    if (!user.visibleToRecruiters && !user.location) {
      handleError("Please set your location in your profile to be visible to recruiters");
      return;
    }
    setUpdating(true);
    accountantApi
      .setVisibility(!user.visibleToRecruiters)
      .then(() => maybeNotSetupRef.current?.setUser?.({...user, visibleToRecruiters: !user.visibleToRecruiters}))
      .finally(() => setUpdating(false));
  };

  const onChangeCertification = (_name: string, updated: Record<string, string | {url: string; imageUrl?: string}>) => {
    setAccountant?.({...user, certifications: {...updated}});
  };

  const onChangeReview = (name: string, url: string) => {
    if (!editable || updating) return;
    setUpdating(true);
    accountantApi
      .updateReviews({reviews: {...user.reviews, [name]: url}})
      .then((updated) => setAccountant?.({...user, reviews: {...updated}}))
      .finally(() => setUpdating(false));
  };

  const onDeleteReview = (name: string) => {
    if (!editable || updating) return;
    setUpdating(true);
    const reviews = {...user.reviews};
    delete reviews[name];
    accountantApi
      .updateReviews({reviews})
      .then((updated) => setAccountant?.({...user, reviews: {...updated}}))
      .finally(() => setUpdating(false));
  };

  const onUpdateProfileField = (field: (typeof editableProfileFields)[number]) => (value: string) => {
    if (!editable || updating) return;
    setUpdating(true);
    const {name, bio, state, city, employmentType, zip, softwareExperience, linkedIn, alignable} = user;
    accountantApi
      .updateProfile({
        name,
        bio,
        state,
        city,
        employmentType,
        zip,
        softwareExperience,
        linkedIn,
        alignable,
        [field]: value,
      })
      .then((updated) => setAccountant?.({...user, ...updated}))
      .finally(() => setUpdating(false));
  };

  const onEdit = () => {
    if (!editable || updating) return;
    setUpdating(true);
    editProfile({
      name: user.name,
      location: user.location,
      city: user.city,
      state: user.state,
      zip: user.zip,
      employmentType: user.employmentType,
      bio: user.bio,
      softwareExperience: user.softwareExperience,
      linkedIn: user.linkedIn,
      alignable: user.alignable,
    })
      .then((edited) => {
        if (edited) maybeNotSetupRef.current?.setUser?.({...user, ...edited});
      })
      .catch(() => {})
      .finally(() => setUpdating(false));
  };

  const onMagic = () => {
    if (openingGym) return;
    if (DASHBOARD_TARGET === "cas") {
      window.location.href = CAS_FRONTEND_URL;
      return;
    }
    setOpeningGym(true);
    accountantApi
      .openGym(user.magic)
      .then(({token}) => {
        const url = new URL("https://courses.akadian.com/free-exercises/roadmap-dashboard");
        url.searchParams.set("booksmatch_token", token);
        url.searchParams.set("magic", user.magic);
        window.open(url.toString(), "_self");
      })
      .finally(() => setOpeningGym(false));
  };

  const onEditPassword = () =>
    editPassword({})
      .then(() => {})
      .catch(() => {});

  const onCopyMagic = () => {
    if (!user.magic) {
      handleError("No magic link available");
      return;
    }
    navigator.clipboard
      .writeText(`https://fastandeasyqbo.booksmatch.us/auth/code/${user.magic}`)
      .then(() => handleError("Magic link copied to clipboard"))
      .catch(() => handleError("Failed to copy magic link"));
  };

  const onShare = () => {
    if (sharing) return;
    if (storageKey) {
      localStorage.setItem(storageKey, skillsHash);
      setIsStale(false);
    }
    setSharing(true);
    accountantApi
      .shareProfile()
      .then((data) => setShareData(data))
      .finally(() => setSharing(false));
  };

  const onSubscribe = () => {
    const sp = new URLSearchParams();
    sp.append("email", user.xpData.email);
    sp.append("first_name", user.xpData.firstName);
    sp.append("last_name", user.xpData.lastName);
    window.open("https://fastandeasyqbo.samcart.com/products/qbo-gym/?" + sp.toString(), "_blank");
  };

  /* ── status chips ────────────────────────────────────────────────────────── */
  const overview = getOverviewConfig(knowledge, activity, speed);
  const statusChips = [
    {
      label: overview.text,
      sublabel:
        activity === "low" ? "Low activity" : activity === "average" ? "Activity inconsistent" : "Knowledge + Activity",
      tooltip: "Overall rating based on your knowledge, activity, and speed",
      color: overview.color,
      icon: <Users className="w-5 h-5" />,
    },
    {
      label: knowledgeLabelMap[knowledge],
      sublabel: "Knowledge",
      tooltip: `${totalSkills} skill${totalSkills !== 1 ? "s" : ""} verified`,
      color: levelColor(knowledge),
      icon: <Star className="w-5 h-5" />,
    },
    {
      label: levelLabel(activity),
      sublabel: activityDescription,
      tooltip: `${addedPastWeek} skill${addedPastWeek !== 1 ? "s" : ""} added in the past 7 days`,
      color: levelColor(activity),
      icon: <Activity className="w-5 h-5" />,
    },
    {
      label: levelLabel(speed),
      sublabel: "Speed",
      tooltip: "How quickly you're advancing through the training environment",
      color: levelColor(speed),
      icon: <Zap className="w-5 h-5" />,
    },
  ];

  /* ── render ──────────────────────────────────────────────────────────────── */
  return (
    <div className="h-full overflow-y-auto overflow-x-hidden bg-brand-cream">
      {shareData && (
        <ShareModal token={shareData.token} shareUrl={shareData.shareUrl} onClose={() => setShareData(null)} />
      )}
      {editable && (
        <>
          {EditProfileModal}
          {EditPasswordModal}
          {CoursePickerModal}
          {SubscribeToGymModal}
          <CertificationPopup />
        </>
      )}

      <div className="px-8 py-7">
        {/* Page heading */}
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <h1 className="text-2xl font-bold text-brand-navy">BooksMatch Profile</h1>

          {!user.enterprise && editable && (
            <div className="flex items-center gap-2">
              <Tooltip
                position="bottom"
                text={isPaidMember ? "" : "You must be a paying member to use this feature"}
                className="text-center"
              >
                <button
                  onClick={onVisibilityToggle}
                  disabled={updating}
                  className={
                    [
                      "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors disabled:opacity-50",
                      updating && "animate-pulse",
                    ].asClass
                  }
                  style={
                    user.visibleToRecruiters
                      ? {backgroundColor: "#E8F5ED", borderColor: "#57B98F", color: "#57B98F"}
                      : {backgroundColor: "#F5F3F0", borderColor: "#E8E4DF", color: "#8490AA"}
                  }
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{backgroundColor: user.visibleToRecruiters ? "#57B98F" : "#8490AA"}}
                  />
                  {user.visibleToRecruiters ? "Open to Work" : "Not Open to Work"}
                </button>
              </Tooltip>

              {user.visibleToRecruiters && (
                <div className="relative">
                  <Tooltip
                    position="bottom"
                    text={
                      isStale
                        ? "Your profile has changed since your last share — regenerate to reflect your latest results."
                        : ""
                    }
                    className="text-center max-w-[220px]"
                  >
                    <button
                      onClick={onShare}
                      disabled={sharing}
                      className="flex items-center gap-2 px-4 py-1.5 rounded-full border border-brand-border bg-white text-sm font-medium text-brand-muted hover:text-brand-navy hover:border-brand-navy transition-colors disabled:opacity-50"
                    >
                      <Share2 className="w-4 h-4" />
                      Share
                    </button>
                  </Tooltip>
                  {isStale && (
                    <span className="absolute top-px right-px w-1.5 h-1.5 rounded-full bg-orange-400 pointer-events-none" />
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {!user.enterprise && <StatusBar chips={statusChips} />}

        <ProfileSection
          user={user}
          editable={editable}
          updating={updating}
          openingGym={openingGym}
          moreInfoOpen={moreInfoOpen}
          setMoreInfoOpen={setMoreInfoOpen}
          missing={missing}
          showCopyMagic={!!showCopyMagic}
          onEdit={onEdit}
          onEditPassword={onEditPassword}
          onMagic={onMagic}
          onCopyMagic={onCopyMagic}
          onChangeCertification={onChangeCertification}
          onUpdateProfileField={onUpdateProfileField}
        />

        {!user.enterprise && (
          <ReviewsRow
            user={user}
            editable={editable}
            updating={updating}
            onChangeReview={onChangeReview}
            onDeleteReview={onDeleteReview}
          />
        )}

        {!!user.enterprise && <ProgressTags tags={user.activityOverview.enterpriseProgressTags || []} />}

        <ProfileTabs
          user={user}
          isPaidMember={isPaidMember}
          onSubscribe={onSubscribe}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      </div>
    </div>
  );
};

export default AccountantProfilePage;
